import * as Yup from 'yup';
import * as R from 'ramda';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import React, { useMemo, useState, useEffect } from 'react';
// components
import { FormFooter2 } from '../../../../components/form-footer';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
import { intervalUomOptions } from '../../../../helpers/options';
// forms
import { Fieldset2 } from '../../../../forms';
// utilities
import { sendRequest } from '../../../../utilities/http';
import endpointsMap from '../../../../utilities/endpoints';
// feature template-report
import { getBranchConfigsRequest, getServiceVendorListAvailableRequest } from '../actions';
import { makeSelectBranchConfigs, makeSelectServiceVendorListAvailable } from '../selectors';
//////////////////////////////////////////////////

const inputWrapperStyles = { mb: 25, width: 270 };

const settings = {
  [GC.FIELD_TEMPLATE_ID]: {
    isRequired: true,
    label: ['titles:template-id'],
    fieldName: GC.FIELD_TEMPLATE_ID,
    inputWrapperStyles: { ...inputWrapperStyles, mr: 50 },
  },
  [GC.FIELD_SERVICE_TYPE]: {
    type: 'select',
    isRequired: true,
    inputWrapperStyles,
    label: ['titles:service-type'],
    fieldName: GC.FIELD_SERVICE_TYPE,
    options: GC.FLEET_EQUIPMENT_SERVICE_TYPE,
  },
  [GC.FIELD_SERVICE_VENDOR_GUID]: {
    type: 'select',
    inputWrapperStyles,
    options: 'serviceVendorOptions',
    label: ['titles:service-vendor'],
    fieldName: GC.FIELD_SERVICE_VENDOR_GUID,
  },
  [GC.FIELD_TRUCK_TYPE]: {
    type: 'select',
    inputWrapperStyles,
    options: GC.TRUCK_TRUCK_TYPE,
    label: ['titles:truck-type'],
    fieldName: GC.FIELD_TRUCK_TYPE,
    disabled: (_: any, { trailerType }: Object) => G.isNotNilAndNotEmpty(trailerType),
    isRequired: (props: Object) => G.isNilOrEmpty(R.path(['values', GC.FIELD_TRAILER_TYPE], props)),
  },
  [GC.FIELD_TRAILER_TYPE]: {
    type: 'select',
    inputWrapperStyles,
    label: ['titles:trailer-type'],
    options: GC.TRAILER_TRAILER_TYPE,
    fieldName: GC.FIELD_TRAILER_TYPE,
    disabled: (_: any, { truckType }: Object) => G.isNotNilAndNotEmpty(truckType),
    isRequired: (props: Object) => G.isNilOrEmpty(R.path(['values', GC.FIELD_TRUCK_TYPE], props)),
  },
  [GC.FIELD_INTERVAL_COUNT]: {
    type: 'text',
    inputWrapperStyles,
    label: ['titles:interval'],
    fieldName: GC.FIELD_INTERVAL_COUNT,
  },
  [GC.FIELD_INTERVAL_UOM]: {
    type: 'select',
    inputWrapperStyles,
    label: ['titles:interval-uom'],
    fieldName: GC.FIELD_INTERVAL_UOM,
    options: G.addEmptyOptionToDropDown(intervalUomOptions),
    isRequired: (props: Object) => G.isNotNilAndNotEmpty(R.path(['values', GC.FIELD_INTERVAL_COUNT], props)),
  },
};

const defaultContactFields = R.map(() => '', settings);

const validationSchema = Yup.lazy(({ truckType, trailerType, intervalCount }: Object) => Yup.object().shape({
  [GC.FIELD_TEMPLATE_ID]: G.yupStringRequired,
  [GC.FIELD_SERVICE_TYPE]: G.yupStringRequired,
  [GC.FIELD_TRUCK_TYPE]: G.isNilOrEmpty(trailerType) ? G.yupStringRequired : G.yupStringNotRequired,
  [GC.FIELD_TRAILER_TYPE]: G.isNilOrEmpty(truckType) ? G.yupStringRequired : G.yupStringNotRequired,
  [GC.FIELD_INTERVAL_UOM]: G.isNotNilAndNotEmpty(intervalCount) ? G.yupStringRequired : G.yupStringNotRequired,
}));

const fieldsWrapperStyles = {
  px: 0,
  mt: 15,
  width: 570,
  justifyContent: 'space-between',
};

const EquipmentServiceTemplateForm = (props: Object) => {
  const { guid, openLoader, closeLoader, submitAction } = props;

  const [saveAndAddNew, setSaveAndAddNew] = useState(false);

  const dispatch = useDispatch();

  const branchConfigs = useSelector(makeSelectBranchConfigs());
  const serviceVendorListAvailable = useSelector(makeSelectServiceVendorListAvailable());

  const onSubmit = (values: Object, { resetForm, setSubmitting }: Object) => {
    const successCallback = () => {
      setSubmitting(false);

      if (saveAndAddNew) {
        resetForm();
        setSaveAndAddNew(false);
      }
    };

    const failCallback = () => setSubmitting(false);

    submitAction({
      ...G.mapObjectEmptyStringFieldsToNull(values),
      failCallback,
      successCallback,
      shouldGetItemList: true,
      shouldCloseModal: G.isFalse(saveAndAddNew),
    });
  };

  const formik = useFormik({
    onSubmit,
    validationSchema,
    initialValues: defaultContactFields,
  });

  const { setValues, isSubmitting, handleSubmit } = formik;

  const serviceVendorOptions = G.addEmptyOptionToDropDown(R.or(serviceVendorListAvailable, []));
  const configNamesArray = [GC.TRUCK_TRUCK_TYPE, GC.TRAILER_TRAILER_TYPE, GC.FLEET_EQUIPMENT_SERVICE_TYPE];

  const configOptions = R.pick(configNamesArray, R.propOr({}, 'dropdownOptions', branchConfigs));

  useEffect(() => {
    if (R.isEmpty(configOptions)) dispatch(getBranchConfigsRequest(configNamesArray));

    if (G.isNilOrEmpty(serviceVendorListAvailable)) dispatch(getServiceVendorListAvailableRequest());

    const handleGetInitialValues = async () => {
      openLoader();

      const endpoint = endpointsMap.fleetEquipmentServiceTemplateByGuid(guid);

      const res = await sendRequest('get', endpoint);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        const mapped = {
          ...data,
          [GC.FIELD_TRUCK_TYPE]: R.path([GC.FIELD_TRUCK_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], data),
          [GC.FIELD_TRAILER_TYPE]: R.path([GC.FIELD_TRAILER_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], data),
          [GC.FIELD_SERVICE_TYPE]: R.path([GC.FIELD_SERVICE_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], data),
        };

        setValues(mapped);
      } else {
        G.handleException('error', 'EquipmentServiceTemplateForm exception');
      }

      closeLoader();
    };

    if (R.isNotNil(guid)) handleGetInitialValues();
  }, []);

  const actionButtons = useMemo(() => {
    if (R.isNotNil(guid)) return [];

    const darkBlueColor = G.getTheme('colors.dark.blue');

    return [
      {
        type: 'submit',
        disabled: isSubmitting,
        action: () => setSaveAndAddNew(true),
        displayText: G.getWindowLocale('titles:save-and-add-new', 'Save and Add New'),
        buttonStyles: {
          mr: 20,
          ml: 'auto',
          bgColor: 'none',
          background: 'none',
          border: '1px solid',
          borderRadius: '5px',
          width: 'max-content',
          textColor: darkBlueColor,
          borderColor: darkBlueColor,
        },
      },
    ];
  }, [isSubmitting]);

  return (
    <form onSubmit={handleSubmit}>
      <Fieldset2
        {...G.getFormikProps(formik)}
        {...configOptions}
        fields={R.values(settings)}
        fieldsWrapperStyles={fieldsWrapperStyles}
        serviceVendorOptions={serviceVendorOptions}
      />
      <FormFooter2
        actionButtons={actionButtons}
        submitDisabled={isSubmitting}
        submitAction={() => setSaveAndAddNew(false)}
      />
    </form>
  );
};

export default EquipmentServiceTemplateForm;
